/**
 * JulietteRightPanel — Surface de conversation Juliette ancrée à droite.
 *
 * Remplace le plein écran (JulietteFullscreen) : ici on PARLE à Juliette sans
 * quitter l'app. Le panneau est docké à droite et POUSSE le contenu (géré dans
 * ProtectedLayout), si bien que l'agent continue à naviguer dans le CRM / la
 * messagerie pendant qu'elle répond.
 *
 * Comme la colonne est étroite (~380px), les surfaces du plein écran
 * (Échange / Signaux / Suivi / Historique) deviennent des ONGLETS — une à la
 * fois — au lieu d'être étalées. Réutilise l'orchestration éprouvée :
 * getOrCreateReactiviteSession + useJulietteVoice + JulietteTextPanel + les
 * listes Signaux / Suivi / Historique existantes.
 *
 * Visible quand le launcher est ouvert en mode "panel".
 */

import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  AlertCircle,
  Mic,
  Keyboard,
  Settings,
  MessageCircle,
  Radar,
  ListChecks,
  History,
  RotateCcw,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { extractAllGotos } from "@/lib/juliette-goto";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import {
  getOrCreateReactiviteSession,
  getOrCreateFreshChatThread,
} from "@/lib/juliette-session";
import { JulietteOrb, type OrbState } from "@/components/juliette/JulietteOrb";
import { MessageFeedback } from "@/components/juliette/MessageFeedback";
import { JulietteTextPanel } from "@/components/juliette/JulietteTextPanel";
import { JulietteThreadTabs } from "@/components/juliette/JulietteThreadTabs";
import { JulietteSignalsList } from "@/components/juliette/JulietteSignalsList";
import { JulietteSuiviList } from "@/components/juliette/JulietteSuiviList";
import { JulietteHistoryList } from "@/components/juliette/JulietteHistoryList";
import { useJulietteLauncher } from "@/hooks/use-juliette-launcher";
import { useJulietteVoice, type VoiceState } from "@/hooks/useJulietteVoice";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

type SurfaceMode = "voice" | "text";
type PanelTab = "echange" | "signaux" | "suivi" | "historique";

const VOICE_STATE_TO_ORB: Record<VoiceState, OrbState> = {
  idle: "idle",
  listening: "listening",
  thinking: "thinking",
  speaking: "speaking",
  // Veille : visuel dédié (respiration calme + halo plus présent).
  standby: "standby",
  error: "idle",
};

const STATUS_LABEL: Record<VoiceState, string> = {
  idle: "En ligne",
  listening: "Je t'écoute…",
  thinking: "Je réfléchis…",
  speaking: "",
  // Veille : invite explicite au clic pour reprendre la conversation.
  standby: "En veille — clique pour reprendre",
  error: "",
};

const TABS: Array<{ key: PanelTab; label: string; icon: typeof MessageCircle }> = [
  { key: "echange", label: "Échange", icon: MessageCircle },
  { key: "signaux", label: "Signaux", icon: Radar },
  { key: "suivi", label: "Suivi", icon: ListChecks },
  { key: "historique", label: "Historique", icon: History },
];

export function JulietteRightPanel() {
  const { isOpen, mode, panelTab, openTick, prefill, kickoff, close } = useJulietteLauncher();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isVisible = isOpen && mode === "panel";

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  const [surfaceMode, setSurfaceMode] = useState<SurfaceMode>("voice");
  const [activeTab, setActiveTab] = useState<PanelTab>("echange");
  const creatingRef = useRef(false);
  const startedRef = useRef(false);
  // Briefing dashboard armé, consommé UNE fois par la surface (voix ou écrit).
  const pendingBriefingRef = useRef<string | null>(null);
  const kickoffTickRef = useRef(0);
  // Miroir d'état : force un re-render quand le briefing est armé/consommé
  // (une ref seule ne réveillerait pas l'effet de démarrage voix).
  const [briefingArmed, setBriefingArmed] = useState(false);

  // « Fais appel à Juliette » : ouvre un fil NEUF (pour ne pas polluer une
  // conversation en cours), cale le mode choisi par l'agent, et arme le briefing
  // que la surface jouera dès que la session est prête.
  useEffect(() => {
    if (!isVisible || !kickoff || !user || kickoffTickRef.current === openTick) return;
    kickoffTickRef.current = openTick;
    setSurfaceMode(kickoff.surface);
    setActiveTab("echange");
    pendingBriefingRef.current = kickoff.deck || null;
    setBriefingArmed(!!kickoff.deck);
    void (async () => {
      try {
        const { data: u } = await supabase
          .from("users")
          .select("team_id")
          .eq("id", user.id)
          .maybeSingle();
        const resolvedTeamId = (u as { team_id?: string } | null)?.team_id;
        if (!resolvedTeamId) throw new Error("team_id introuvable");
        setTeamId(resolvedTeamId);
        const id = await getOrCreateFreshChatThread(resolvedTeamId, "dashboard_briefing");
        setSessionId(id);
        void queryClient.invalidateQueries({ queryKey: ["juliette-tabs", resolvedTeamId] });
      } catch (err) {
        setBootError(err instanceof Error ? err.message : String(err));
        pendingBriefingRef.current = null;
        setBriefingArmed(false);
      }
    })();
  }, [isVisible, kickoff, openTick, user, queryClient]);

  // Crée/reprend la session réactivité à l'ouverture.
  // (Sauté quand un briefing est en cours : c'est l'effet ci-dessus qui ouvre le fil.)
  useEffect(() => {
    if (kickoff) return;
    if (!isVisible || sessionId || creatingRef.current || !user) return;
    creatingRef.current = true;
    void (async () => {
      try {
        const { data: u } = await supabase
          .from("users")
          .select("team_id")
          .eq("id", user.id)
          .maybeSingle();
        const resolvedTeamId = (u as { team_id?: string } | null)?.team_id;
        if (!resolvedTeamId) throw new Error("team_id introuvable");
        const id = await getOrCreateReactiviteSession(resolvedTeamId, "sidebar_orb");
        setTeamId(resolvedTeamId);
        setSessionId(id);
      } catch (err) {
        setBootError(err instanceof Error ? err.message : String(err));
      } finally {
        creatingRef.current = false;
      }
    })();
  }, [isVisible, sessionId, user]);

  // Le routage serveur (fils par client) a fait le tour dans un AUTRE fil que
  // celui affiche : on suit — l'onglet actif change, les messages se rechargent
  // seuls (query + realtime sont branches sur sessionId). Defini AVANT le hook
  // voix qui le recoit en option.
  const handleSessionRouted = (id: string) => {
    if (!id) return;
    setSessionId((current) => (current === id ? current : id));
    void queryClient.invalidateQueries({ queryKey: ["juliette-tabs", teamId] });
  };

  const voice = useJulietteVoice({
    sessionId,
    // Fenêtre active réduite à 10s (UX : silence court = veille, pas fermeture).
    silenceTimeoutMs: 10000,
    // Sur mobile : auto-ferme depuis la veille après 90s. PC : reste en veille.
    standbyAutoCloseMs: isMobile ? 90000 : undefined,
    onSessionRouted: handleSessionRouted,
    // Navigation au fil de la voix (mode demo, plusieurs ecrans par reponse) :
    // l'ecran change a l'instant ou sa voix commence a le decrire. La garde
    // "meme route = no-op" rend ce chemin convergent avec la navigation de fin
    // de tour ci-dessous, quel que soit l'ordre d'arrivee.
    onSegmentStart: (segment) => {
      const { routes } = extractAllGotos(segment);
      const route = routes[0];
      if (route && route !== window.location.pathname) navigate(route);
    },
    onClose: () => {
      close();
      setSessionId(null);
    },
  });

  // Démarre l'écoute : seulement sur l'onglet Échange, en mode voix, session prête.
  // Garde par ref : start() n'a pas de protection anti-réentrance interne et joue
  // un greeting audible — on garantit donc UN SEUL start() par cycle "armé".
  // Le garde se réarme dès qu'on quitte l'état exploitable (texte, autre onglet,
  // panneau fermé), pour pouvoir relancer la voix ensuite.
  useEffect(() => {
    const canRun =
      isVisible && !!sessionId && activeTab === "echange" && surfaceMode === "voice";
    if (canRun && !startedRef.current && voice.state === "idle") {
      startedRef.current = true;
      // Briefing armé → elle déroule la file au lieu de dire « je t'écoute ».
      const deck = pendingBriefingRef.current;
      pendingBriefingRef.current = null;
      if (deck) setBriefingArmed(false);
      void voice.start(deck ? { briefingDeck: deck } : undefined);
    } else if (!canRun) {
      startedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, sessionId, surfaceMode, activeTab, briefingArmed]);

  // Coupe le micro hors mode voix OU hors onglet Échange (pas de micro chaud
  // pendant qu'on consulte Signaux / Suivi / Historique).
  useEffect(() => {
    if (surfaceMode !== "voice" || activeTab !== "echange") voice.pause();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surfaceMode, activeTab]);

  // A l'ouverture, se cale sur l'onglet demande par le launcher (ex : "signaux"
  // depuis le whisper CRM). `openTick` (incremente a CHAQUE open()) garantit le
  // re-cale meme si on re-clique le whisper sur le meme onglet alors que le
  // panneau est deja ouvert et que l'agent avait bascule manuellement ailleurs.
  // Les bascules manuelles ne changent ni panelTab ni openTick → pas ecrasees.
  useEffect(() => {
    if (isVisible) setActiveTab(panelTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, panelTab, openTick]);

  // Si l'ouverture fournit un brouillon pre-rempli (ex : "Demander a Juliette"
  // depuis la fiche contact), on bascule en mode ecrit : le brouillon est ecrit,
  // pas dicte, et JulietteTextPanel (le seul a porter le textarea) doit etre rendu
  // pour recevoir le prefill. Au changement d'openTick uniquement.
  useEffect(() => {
    if (isVisible && prefill) setSurfaceMode("text");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTick]);

  // Reset en quittant le panneau.
  useEffect(() => {
    if (!isVisible && sessionId) {
      voice.pause();
      setSessionId(null);
      setBootError(null);
      setSurfaceMode("voice");
      setActiveTab("echange");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const handleClose = () => {
    voice.pause();
    setSessionId(null);
    setBootError(null);
    setSurfaceMode("voice");
    setActiveTab("echange");
    close();
  };

  const handleOpenSettings = () => {
    handleClose();
    navigate("/settings/juliette");
  };

  const handleNewChat = async () => {
    if (!teamId) return;
    voice.pause();
    try {
      // Garde anti-doublons : reutilise un fil General vierge s'il existe deja
      // (cliquer "+" plusieurs fois sans ecrire ne cree pas de coquilles vides).
      const id = await getOrCreateFreshChatThread(teamId, "sidebar_orb");
      setSessionId(id);
      setSurfaceMode("text");
      setActiveTab("echange");
      void queryClient.invalidateQueries({ queryKey: ["juliette-threads", teamId] });
      void queryClient.invalidateQueries({ queryKey: ["juliette-tabs", teamId] });
    } catch (err) {
      setBootError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleSelectThread = (id: string) => {
    voice.pause();
    setSessionId(id);
    setSurfaceMode("text");
    setActiveTab("echange");
  };

  const handleThreadDeleted = (id: string) => {
    void queryClient.invalidateQueries({ queryKey: ["juliette-tabs", teamId] });
    if (id === sessionId) {
      voice.pause();
      setSessionId(null);
    }
  };

  const lastTurn = voice.transcript[voice.transcript.length - 1];

  // Navigation guidee en mode VOCAL, au "done" du flux SSE (assistantMessageId
  // n'est renseigne qu'a ce moment-la). UNIQUEMENT pour une reponse a UN seul
  // ecran : avec plusieurs [GOTO:] (mode demo), naviguer ici sauterait a la
  // destination finale pendant que sa voix decrit encore le premier ecran —
  // c'est onSegmentStart (hook voix) qui pilote, ecran par ecran, au rythme de
  // la lecture. Sur une reponse mono-ecran les deux chemins visent la meme
  // route : la garde "meme route = no-op" absorbe le doublon.
  const lastVoiceNavRef = useRef<string | null>(null);
  useEffect(() => {
    const id = lastTurn?.assistantMessageId;
    if (!id || lastVoiceNavRef.current === id) return;
    lastVoiceNavRef.current = id;
    const { routes } = extractAllGotos(lastTurn?.assistant ?? "");
    if (routes.length !== 1) return;
    const route = routes[0];
    if (route !== window.location.pathname) navigate(route);
  }, [lastTurn?.assistantMessageId, lastTurn?.assistant, navigate]);

  const orbState = VOICE_STATE_TO_ORB[voice.state];
  const statusLabel =
    surfaceMode === "voice" ? STATUS_LABEL[voice.state] : "Mode écrit · silencieux";

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.aside
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ duration: 0.28, ease: "easeOut" }}
          className="fixed right-0 top-0 z-50 flex h-[100dvh] w-full flex-col border-l border-white/10 bg-slate-950 md:w-[380px]"
          aria-label="Conversation avec Juliette"
        >
          <div className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-b from-slate-950 via-indigo-950/40 to-slate-950" />

          {/* Header */}
          <div className="relative z-10 flex shrink-0 items-center justify-between px-3 pt-[calc(env(safe-area-inset-top)+0.5rem)] pb-2">
            <div className="flex items-center gap-2.5">
              <JulietteOrb asAvatar size="sm" state={orbState} ariaLabel="Juliette" />
              <div className="flex flex-col leading-none">
                <span className="font-juliette text-base text-white/95">Juliette</span>
                <span className="mt-1 text-[10.5px] tracking-wide text-white/40" aria-live="polite">
                  {activeTab === "echange" ? statusLabel || "En conversation" : "Je veille"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {activeTab === "echange" && (
                <ModeToggle mode={surfaceMode} onChange={setSurfaceMode} />
              )}
              <button
                type="button"
                onClick={handleOpenSettings}
                aria-label="Réglages de Juliette"
                title="Réglages de Juliette"
                className="flex h-9 w-9 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              >
                <Settings className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={handleClose}
                aria-label="Fermer Juliette"
                title="Fermer"
                className="flex h-9 w-9 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Onglets de conversation (un fil par client) — vue Échange seulement */}
          {activeTab === "echange" && teamId && (
            <JulietteThreadTabs
              teamId={teamId}
              activeSessionId={sessionId}
              onSelect={handleSelectThread}
              onNewChat={() => void handleNewChat()}
            />
          )}

          {/* Corps selon l'onglet */}
          {activeTab === "echange" ? (
            surfaceMode === "voice" ? (
              <div className="relative z-10 flex min-h-0 flex-1 flex-col items-center justify-center gap-6 px-5 pb-6">
                {/* Orbe cliquable uniquement en veille pour reprendre la conversation. */}
                <button
                  type="button"
                  onClick={() => voice.resume()}
                  disabled={voice.state !== "standby"}
                  aria-label={voice.state === "standby" ? "Reprendre la conversation" : "Juliette"}
                  className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-default"
                >
                  <JulietteOrb asAvatar size={140} state={orbState} glow ariaLabel="Juliette en conversation" />
                </button>
                {statusLabel && (
                  <p
                    className="font-juliette text-center text-base italic tracking-wide text-white/70"
                    aria-live="polite"
                  >
                    {statusLabel}
                  </p>
                )}
                {bootError && (
                  <div className="flex items-center gap-2 text-sm text-rose-300">
                    <AlertCircle className="h-4 w-4" />
                    <span>{bootError}</span>
                  </div>
                )}
                {voice.micIssue ? (
                  <div className="flex w-full max-w-xs flex-col items-center gap-3 rounded-2xl border border-rose-400/20 bg-rose-950/30 px-4 py-4 text-center">
                    <AlertCircle className="h-5 w-5 shrink-0 text-rose-300" aria-hidden="true" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-white/90">{voice.micIssue.title}</p>
                      <p className="text-xs leading-relaxed text-white/60">
                        {voice.micIssue.description}
                      </p>
                    </div>
                    <div className="flex w-full flex-col gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => setSurfaceMode("text")}
                        className="flex items-center justify-center gap-2 rounded-full bg-gradient-to-br from-indigo-400 to-violet-600 px-4 py-2 text-sm font-medium text-white shadow transition-opacity hover:opacity-90"
                      >
                        <Keyboard className="h-4 w-4" aria-hidden="true" />
                        Écrire à Juliette
                      </button>
                      <button
                        type="button"
                        onClick={() => void voice.start()}
                        className="flex items-center justify-center gap-2 rounded-full px-4 py-2 text-xs text-white/55 transition-colors hover:text-white"
                      >
                        <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                        Réessayer
                      </button>
                    </div>
                  </div>
                ) : voice.error && voice.state === "error" ? (
                  <div className="flex items-center gap-2 text-sm text-rose-300">
                    <AlertCircle className="h-4 w-4" />
                    <span>{voice.error}</span>
                  </div>
                ) : null}
                <div className="flex min-h-[3rem] w-full flex-col items-center gap-2">
                  {lastTurn?.user && (
                    <motion.p
                      key={`u-${lastTurn.user}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="flex items-start justify-center gap-1.5 text-center text-sm italic leading-relaxed text-white/55"
                    >
                      <span className="sr-only">Tu as dit&nbsp;:</span>
                      <Mic className="mt-0.5 h-3.5 w-3.5 shrink-0 text-white/40" aria-hidden="true" />
                      <span>« {lastTurn.user} »</span>
                    </motion.p>
                  )}
                  {lastTurn?.assistant && (
                    <motion.p
                      key={lastTurn.assistant}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="text-center text-sm leading-relaxed text-white/85"
                    >
                      {/* Les marqueurs de navigation ne doivent jamais s'afficher :
                          ils sont deja retires du TTS cote serveur (tts-text.ts). */}
                      {extractAllGotos(lastTurn.assistant).text}
                    </motion.p>
                  )}
                  {lastTurn?.assistantMessageId && teamId && sessionId && voice.state !== "thinking" && (
                    <MessageFeedback
                      key={lastTurn.assistantMessageId}
                      messageId={lastTurn.assistantMessageId}
                      sessionId={sessionId}
                      teamId={teamId}
                      initial={null}
                    />
                  )}
                </div>
              </div>
            ) : sessionId && teamId ? (
              <div className="relative z-10 flex min-h-0 flex-1 flex-col">
                <JulietteTextPanel
                  sessionId={sessionId}
                  teamId={teamId}
                  briefingDeck={surfaceMode === "text" && briefingArmed ? pendingBriefingRef.current : null}
                  onBriefingConsumed={() => {
                    pendingBriefingRef.current = null;
                    setBriefingArmed(false);
                  }}
                  onSessionRouted={handleSessionRouted}
                  onUserSent={() =>
                    void queryClient.invalidateQueries({ queryKey: ["juliette-threads", teamId] })
                  }
                />
              </div>
            ) : (
              <div className="relative z-10 flex flex-1 items-center justify-center text-sm text-white/40">
                {bootError ?? "Préparation…"}
              </div>
            )
          ) : activeTab === "signaux" ? (
            <div className="relative z-10 flex-1 overflow-y-auto px-3 py-3">
              <JulietteSignalsList
                teamId={teamId}
                enabled={isVisible}
                variant="dark"
                max={20}
                onNavigated={handleClose}
              />
            </div>
          ) : activeTab === "suivi" ? (
            <div className="relative z-10 flex-1 overflow-y-auto px-3 py-3">
              <JulietteSuiviList teamId={teamId} enabled={isVisible} />
            </div>
          ) : (
            <div className="relative z-10 flex min-h-0 flex-1 flex-col">
              <JulietteHistoryList
                teamId={teamId}
                enabled={isVisible}
                currentSessionId={sessionId}
                onSelect={handleSelectThread}
                onNewChat={() => void handleNewChat()}
                onDeleted={handleThreadDeleted}
              />
            </div>
          )}

          {/* Onglets (en bas — accès au pouce sur mobile, identique sur PC) */}
          <div className="relative z-10 flex shrink-0 gap-1 border-t border-white/10 px-2 pt-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)]">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  aria-pressed={active}
                  className={cn(
                    "flex flex-1 flex-col items-center gap-1 rounded-lg py-1.5 text-[10px] font-medium transition-colors",
                    active
                      ? "bg-white/[0.07] text-white"
                      : "text-white/45 hover:text-white/80",
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}

// ─── Bascule voix / texte ────────────────────────────────────────────────

function ModeToggle({
  mode,
  onChange,
}: {
  mode: SurfaceMode;
  onChange: (m: SurfaceMode) => void;
}) {
  return (
    <div className="flex items-center rounded-full border border-white/12 bg-white/[0.06] p-0.5 backdrop-blur-sm">
      <button
        type="button"
        onClick={() => onChange("voice")}
        aria-label="Mode voix"
        aria-pressed={mode === "voice"}
        className={cn(
          "flex h-7 w-7 items-center justify-center rounded-full transition-all",
          mode === "voice"
            ? "bg-gradient-to-br from-indigo-400 to-violet-600 text-white shadow"
            : "text-white/55 hover:text-white",
        )}
      >
        <Mic className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => onChange("text")}
        aria-label="Mode écrit"
        aria-pressed={mode === "text"}
        className={cn(
          "flex h-7 w-7 items-center justify-center rounded-full transition-all",
          mode === "text"
            ? "bg-gradient-to-br from-indigo-400 to-violet-600 text-white shadow"
            : "text-white/55 hover:text-white",
        )}
      >
        <Keyboard className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
